import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import { useLocalization } from "@fluent/react";
import { Drawer } from "@narmi/design_system";
import { entities } from "byzantine";
import { format } from "date-fns";
import { CashbackOfferView } from "../CardOfferScrollView";
import Loading from "../../Loading";

import styles from "./CardOfferDetailsDrawer.module.scss";
import Constants from "./constants";

const {
  getOfferRequirementsDisplay,
  getPurchaseChannelDisplay,
  useAllCardLinkedOffers,
  useOfferById,
} = entities.cardLinkedOffers;
interface CardOfferDetailsDrawerProps {
  isOpen: boolean;
  offerId?: string;
  onUserDismiss: () => void;
  onOfferClick?: (offerId: string) => void;
}

const CardOfferDetailsDrawer = ({
  isOpen,
  offerId,
  onUserDismiss,
  onOfferClick,
}: CardOfferDetailsDrawerProps) => {
  const allOffers = useAllCardLinkedOffers();
  const { l10n } = useLocalization();
  const offer = useOfferById(offerId || "");
  const containerRef = useRef<HTMLDivElement>(null);

  // Loading states for images
  const [logoLoading, setLogoLoading] = useState(!!offer?.logoUrl);
  const [bannerLoading, setBannerLoading] = useState(!!offer?.bannerUrl);

  // Terms expansion state
  const [termsExpanded, setTermsExpanded] = useState(false);

  // Reset loading states when offer changes
  useEffect(() => {
    let logoTimeout: NodeJS.Timeout | undefined;
    let bannerTimeout: NodeJS.Timeout | undefined;

    if (offer?.logoUrl) {
      setLogoLoading(true);
      // Fallback timeout to prevent infinite loading
      logoTimeout = setTimeout(() => {
        setLogoLoading(false);
      }, 10000); // 10 seconds timeout
    } else {
      setLogoLoading(false);
    }

    if (offer?.bannerUrl) {
      setBannerLoading(true);
      // Fallback timeout to prevent infinite loading
      bannerTimeout = setTimeout(() => {
        setBannerLoading(false);
      }, 10000); // 10 seconds timeout
    } else {
      setBannerLoading(false);
    }

    // Reset terms expansion when offer changes
    setTermsExpanded(false);

    return () => {
      if (logoTimeout) clearTimeout(logoTimeout);
      if (bannerTimeout) clearTimeout(bannerTimeout);
    };
  }, [offer?.logoUrl, offer?.bannerUrl, offer?.id]);

  const formatDate = useCallback(
    (dateString: string) =>
      dateString ? format(new Date(dateString), "MM/dd/yyyy") : "",
    []
  );

  // Image loading handlers
  const handleLogoLoad = useCallback(() => {
    setLogoLoading(false);
  }, []);

  const handleBannerLoad = useCallback(() => {
    setBannerLoading(false);
  }, []);

  const handleLogoError = useCallback(() => {
    setLogoLoading(false);
  }, []);

  const handleBannerError = useCallback(() => {
    setBannerLoading(false);
  }, []);

  const handleTermsToggle = useCallback(() => {
    setTermsExpanded((prev) => !prev);
  }, []);

  // Handle offer click with scroll to top
  const handleOfferClick = useCallback(
    (newOfferId: string) => {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }

      // Call the original onOfferClick handler
      onOfferClick?.(newOfferId);
    },
    [onOfferClick]
  );

  // Check if terms are long enough to potentially need truncation (rough estimate)
  const shouldShowReadMore = useMemo(
    () => offer?.terms && offer.terms.length > Constants.MAX_VISIBLE_CHARACTERS,
    [offer?.terms]
  );

  // Check if we should show the details section
  const hasDetailsToShow = useMemo(
    () =>
      !!(
        (offer && getOfferRequirementsDisplay(offer)) ||
        offer?.expirationDate ||
        (offer?.purchaseChannels && offer.purchaseChannels.length > 0)
      ),
    [offer]
  );

  // Get other offers for "Explore more offers" section (offers with shared categories)
  const otherOffers = useMemo(() => {
    if (!offer) return [];

    return allOffers.filter((o) => {
      // Exclude current offer
      if (o.id === offer.id) return false;

      // Check if this offer shares any categories with the current offer
      return offer.categories.some((category) =>
        o.categories.includes(category)
      );
    });
  }, [allOffers, offer]);

  if (!offer) {
    return null;
  }

  return (
    <Drawer
      depth="622px"
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      showControls={false}
      paddingSize="none"
      data-testid="card-offer-details-drawer"
    >
      <div className={styles.container} ref={containerRef}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.merchantLogo}>
              {offer.logoUrl ? (
                <>
                  <img
                    src={offer.logoUrl}
                    alt={offer.name}
                    onLoad={handleLogoLoad}
                    onError={handleLogoError}
                    style={{ display: logoLoading ? "none" : "block" }}
                  />
                  {logoLoading && <div className={styles.logoLoading} />}
                </>
              ) : (
                <div className={styles.placeholderLogo}>
                  {offer.name.charAt(0)}
                </div>
              )}
            </div>
            <div className={styles.merchantName}>{offer.name}</div>
          </div>
        </div>

        {/* Offer Banner */}
        <div className={styles.bannerContainer}>
          {offer.bannerUrl ? (
            <>
              <img
                src={offer.bannerUrl}
                alt={`${offer.name} offer banner`}
                className={styles.bannerImage}
                onLoad={handleBannerLoad}
                onError={handleBannerError}
                style={{ display: bannerLoading ? "none" : "block" }}
              />
              {bannerLoading && (
                <div className={styles.bannerLoading}>
                  <Loading />
                </div>
              )}
            </>
          ) : (
            <div className={styles.placeholderBanner}>
              <div className={styles.placeholderContent}>
                <div className={styles.placeholderLogo}>
                  {offer.name.charAt(0)}
                </div>
                <span>{offer.name}</span>
              </div>
            </div>
          )}
        </div>

        {/* Auto-added status */}
        <div className={styles.autoAddedStatus}>
          <div className={styles.checkIcon}>
            <span className="narmi-icon-check" />
          </div>
          <span className={styles.autoAddedText}>
            {l10n.getString(
              "card-offer-auto-added",
              null,
              "Automatically added to your card"
            )}
          </span>
        </div>

        {/* Offer Details */}
        {hasDetailsToShow && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              {l10n.getString("card-offer-details", null, "Offer Details")}
            </div>
            <div className={styles.detailsCard}>
              {offer && getOfferRequirementsDisplay(offer) && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>
                    {l10n.getString("card-offer-detail-offer", null, "Offer")}
                  </span>
                  <span className={styles.detailValue}>
                    {getOfferRequirementsDisplay(offer)}
                  </span>
                </div>
              )}
              {offer.expirationDate && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>
                    {l10n.getString(
                      "card-offer-detail-expiration",
                      null,
                      "Expiration"
                    )}
                  </span>
                  <span className={styles.detailValue}>
                    {formatDate(offer.expirationDate)}
                  </span>
                </div>
              )}
              {offer.purchaseChannels && offer.purchaseChannels.length > 0 && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>
                    {l10n.getString("card-offer-detail-usage", null, "Usage")}
                  </span>
                  <span className={styles.detailValue}>
                    {getPurchaseChannelDisplay(offer.purchaseChannels)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Description */}
        {offer.description && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              {l10n.getString("card-offer-description", null, "Description")}
            </div>
            <div className={styles.descriptionContent}>{offer.description}</div>
          </div>
        )}

        {/* Terms and Conditions */}
        {offer.terms && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              {l10n.getString("card-offer-terms", null, "Terms and Conditions")}
            </div>
            <div
              className={`${styles.termsContent} ${
                termsExpanded ? styles.expanded : styles.truncated
              }`}
            >
              {offer.terms}
            </div>
            {shouldShowReadMore && (
              <button
                className={styles.readMoreButton}
                onClick={handleTermsToggle}
                type="button"
              >
                {termsExpanded
                  ? l10n.getString("card-offer-read-less", null, "Read less")
                  : l10n.getString("card-offer-read-more", null, "Read more")}
              </button>
            )}
          </div>
        )}

        {/* Explore More Offers - only show if there are offers with overlapping categories */}
        {otherOffers.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              {l10n.getString(
                "card-offer-explore-more",
                null,
                "Explore more offers"
              )}
            </div>
            <CashbackOfferView
              offers={otherOffers}
              maxCards={8}
              onOfferClick={handleOfferClick}
            />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default CardOfferDetailsDrawer;
